
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Eye, Truck, Check, X, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface ShippingInfo {
  trackingNumber?: string;
  carrier?: string;
  shippingDate?: string;
  estimatedDelivery?: string;
}

interface OrderActionsProps {
  orderId: string;
  listingId?: number;
  status: string;
  isUpdating: boolean;
  onViewListing: (listingId: number) => void;
  onUpdateStatus: (orderId: string, newStatus: string, shippingInfo?: ShippingInfo) => void;
  onCancelOrder?: (orderId: string) => void;
  needsCancellationConfirmation?: boolean;
}

const OrderActions = ({
  orderId,
  listingId,
  status,
  isUpdating,
  onViewListing,
  onUpdateStatus,
  onCancelOrder,
  needsCancellationConfirmation
}: OrderActionsProps) => {
  const [isShippingDialogOpen, setIsShippingDialogOpen] = useState(false);
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo>({
    trackingNumber: '',
    carrier: '',
    shippingDate: new Date().toISOString().split('T')[0],
    estimatedDelivery: ''
  });
  
  const handleShippingInfoSubmit = () => {
    // Make sure we have at least a tracking number
    if (!shippingInfo.trackingNumber?.trim()) return;
    
    onUpdateStatus(orderId, 'shipped', shippingInfo);
    setIsShippingDialogOpen(false);
  };
  
  // Actions allowed based on current status
  const canMarkAsProcessing = status === 'pending';
  const canMarkAsShipped = status === 'processing';
  const canCancel = ['pending', 'processing'].includes(status) && !needsCancellationConfirmation && onCancelOrder;
  
  
  
  return (
    <div className="flex flex-wrap gap-2 mt-4 justify-end">
      {/* View Product button */}
      {listingId && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => onViewListing(listingId)}
        >
          <Eye className="h-4 w-4 mr-1" />
          Shiko Produktin
        </Button>
      )}
      
      {/* Mark as Processing button */}
      {canMarkAsProcessing && (
        <Button 
          variant="default" 
          size="sm" 
          onClick={() => onUpdateStatus(orderId, 'processing')}
          disabled={isUpdating}
          className="bg-blue-500 hover:bg-blue-600"
        >
          <Check className="h-4 w-4 mr-1" />
          Konfirmo Porosinë
        </Button>
      )}
      
      {/* Mark as Shipped button */}
      {canMarkAsShipped && (
        <Button 
          variant="default" 
          size="sm" 
          onClick={() => setIsShippingDialogOpen(true)}
          disabled={isUpdating}
          className="bg-purple-500 hover:bg-purple-600"
        >
          <Truck className="h-4 w-4 mr-1" />
          Shëno si të Dërguar
        </Button>
      )}
      
      {/* Cancel Order button */}
      {canCancel && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => onCancelOrder?.(orderId)}
          disabled={isUpdating}
          className="text-red-500 border-red-500 hover:bg-red-50"
        >
          <X className="h-4 w-4 mr-1" />
          Anulo Porosinë
        </Button>
      )}
      
      {/* Shipping dialog */}
      <Dialog open={isShippingDialogOpen} onOpenChange={setIsShippingDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Detajet e Transportit</DialogTitle>
            <DialogDescription>
              Ju lutemi jepni informacionin e transportit për këtë porosi.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="trackingNumber" className="text-right">
                Numri i Gjurmimit
              </Label>
              <Input
                id="trackingNumber"
                value={shippingInfo.trackingNumber}
                onChange={(e) => setShippingInfo({...shippingInfo, trackingNumber: e.target.value})}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="carrier" className="text-right">
                Transportuesi
              </Label>
              <Input
                id="carrier"
                value={shippingInfo.carrier}
                onChange={(e) => setShippingInfo({...shippingInfo, carrier: e.target.value})}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="shippingDate" className="text-right">
                Data e Dërgimit
              </Label>
              <Input
                id="shippingDate"
                type="date"
                value={shippingInfo.shippingDate}
                onChange={(e) => setShippingInfo({...shippingInfo, shippingDate: e.target.value})}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="estimatedDelivery" className="text-right">
                Dorëzimi i Llogaritur
              </Label>
              <Input
                id="estimatedDelivery"
                type="date"
                value={shippingInfo.estimatedDelivery}
                onChange={(e) => setShippingInfo({...shippingInfo, estimatedDelivery: e.target.value})}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsShippingDialogOpen(false)}>Anulo</Button>
            <Button onClick={handleShippingInfoSubmit}>Ruaj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderActions;
